'use strict';

var jsxRuntime = require('@emotion/react/jsx-runtime');
var react = require('@emotion/react');
var React = require('react');
var Icon_web = require('../../atoms/Icon/Icon.web.cjs');
var Skeleton_web = require('../../atoms/Skeleton/Skeleton.web.cjs');
var Typography_web = require('../../atoms/Typography/Typography.web.cjs');
var InternalButtonLink_web = require('../../internal/InternalButtonLink/InternalButtonLink.web.cjs');
var ActionTile_web_styles = require('./ActionTile.web.styles.cjs');

// Constants for line detection
const FOUR_LINE_THRESHOLD = 3.5; // Height multiplier to detect 4+ lines
const RESIZE_DEBOUNCE_MS = 150; // Debounce delay for resize calculations
/**
 * An interactive tile that combines an icon, label, and description to present actions or navigation in a visually engaging way
 *
 * {@link https://aviary.fullscript.io/latest/components/action-tile/web-dDY6d7yF | Documentation}
 *
 * {@link https://git.fullscript.io/developers/aviary/-/blob/development/packages/aviary-web/src/components/molecules/ActionTile/ActionTile.web.tsx | View in GitLab}
 *
 * @scope Web
 */ const ActionTile = ({ label, content, icon, intention = "neutral", showBorder = false, isLoading = false, variant = "fill", customCss, ...props })=>{
    const theme = react.useTheme();
    const textContainerRef = React.useRef(null);
    const [hasFourOrMoreLines, setHasFourOrMoreLines] = React.useState(false);
    React.useEffect(()=>{
        if (isLoading) return;
        let debounceTimer;
        const checkLineCount = ()=>{
            if (!textContainerRef.current) {
                setHasFourOrMoreLines(false);
                return;
            }
            const element = textContainerRef.current;
            const lineHeight = parseFloat(theme.ds5.typography.lineHeight.body); // Aligned with ActionTile's Typography sizing
            const height = element.scrollHeight;
            // Align to top if there are 4 or more lines
            setHasFourOrMoreLines(height >= lineHeight * FOUR_LINE_THRESHOLD);
        };
        // Initial check
        checkLineCount();
        // Debounced resize observer to avoid excessive calculations
        const resizeObserver = new ResizeObserver(()=>{
            clearTimeout(debounceTimer);
            debounceTimer = setTimeout(checkLineCount, RESIZE_DEBOUNCE_MS);
        });
        if (textContainerRef.current) {
            resizeObserver.observe(textContainerRef.current);
        }
        return ()=>{
            clearTimeout(debounceTimer);
            resizeObserver.disconnect();
        };
    }, [
        content,
        label,
        theme,
        isLoading
    ]);
    const memoizedStyles = React.useMemo(()=>({
            container: [
                ActionTile_web_styles.wrapper(theme, hasFourOrMoreLines),
                variant === "fill" ? ActionTile_web_styles.wrapperBackground[intention](theme) : ActionTile_web_styles.wrapperBackground.noFill(theme),
                ActionTile_web_styles.border(theme, showBorder, intention),
                customCss
            ],
            iconWrapper: ActionTile_web_styles.iconWrapper,
            textContainer: ActionTile_web_styles.textContainer,
            label: ActionTile_web_styles.label(theme, intention),
            content: ActionTile_web_styles.content(theme, intention)
        }), [
        theme,
        intention,
        showBorder,
        variant,
        customCss,
        hasFourOrMoreLines
    ]);
    if (isLoading) {
        return /*#__PURE__*/ jsxRuntime.jsx(Skeleton_web.Skeleton, {
            isFullWidth: true,
            height: "105px"
        });
    }
    return /*#__PURE__*/ jsxRuntime.jsxs(InternalButtonLink_web.InternalButtonLink, {
        css: memoizedStyles.container,
        "data-testid": "aviary-action-tile",
        ...props,
        children: [
            icon?.icon && /*#__PURE__*/ jsxRuntime.jsx("div", {
                css: memoizedStyles.iconWrapper,
                children: /*#__PURE__*/ jsxRuntime.jsx(Icon_web.Icon, {
                    variant: "circular",
                    intention: intention === "accentGreen" ? "success" : "neutral",
                    "aria-hidden": "true",
                    ...icon
                })
            }),
            /*#__PURE__*/ jsxRuntime.jsxs("div", {
                ref: textContainerRef,
                css: memoizedStyles.textContainer,
                children: [
                    label && /*#__PURE__*/ jsxRuntime.jsx(Typography_web.Typography, {
                        fontWeight: "bold",
                        isMarginless: true,
                        customCss: memoizedStyles.label,
                        children: label
                    }),
                    content && /*#__PURE__*/ jsxRuntime.jsx(Typography_web.Typography, {
                        isMarginless: true,
                        customCss: memoizedStyles.content,
                        children: content
                    })
                ]
            })
        ]
    });
};

exports.ActionTile = ActionTile;
//# sourceMappingURL=ActionTile.web.cjs.map
